import { toRaw } from 'vue';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  IBodyTax,
  IBodyTaxUpdate,
  ITaxeResponse,
} from '@/types/taxe.types';
import { taxe_id, taxe_page } from '@/refs/taxe.refs';
import { commerceId } from '@/refs/commerce.refs';
// import { restaurantId } from "@/refs/common.refs";

export const TaxeService = {
  /**
   * Crée une nouvelle taxe dans le système.
   *
   * @async
   * @function createTaxe
   * @param {IBodyTax} data - Données de la taxe à enregistrer.
   * @returns {Promise<any>} La réponse de l'API après la création de la taxe.
   * @throws {AxiosError} Lance une erreur si la requête échoue.
   */
  createTaxe: async (data: IBodyTax) => {
    try {
      const response = await axiosInstance.post(`taxes/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste paginée des taxes.
   *
   * @async
   * @function getTaxe
   * @returns {Promise<ITaxeResponse>} Données paginées des taxes retournées par l'API.
   * @throws {AxiosError} Lance une erreur si la requête échoue.
   */
  getTaxe: async () => {
    try {
      const page = Number(toRaw(taxe_page.value));

      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());
      params.append('storeId', commerceId.value); // Ajout possible si besoin

      const response = await axiosInstance.get<ITaxeResponse>(
        `taxes/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère une taxe spécifique à partir de son identifiant.
   *
   * @async
   * @function getTaxeById
   * @returns {Promise<any | undefined>}
   *          Retourne les données de la taxe depuis l'API,
   *          ou `undefined` si `taxe_id` est null.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue.
   */
  getTaxeById: async () => {
    try {
      if (taxe_id.value === null) return;
      const id = toRaw(taxe_id.value.value);
      const response = await axiosInstance.get<any>(`taxes/get/${id}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
  /**
   * Met à jour une taxe existante par son identifiant.
   *
   * @async
   * @function updateTaxById
   * @param {Partial<IBodyTaxUpdate>} taxeData - Données partielles de la taxe à mettre à jour.
   * @returns {Promise<any | undefined>}
   *          Réponse de l'API après la mise à jour, ou `undefined` si `taxe_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête échoue.
   */
  updateTaxById: async (taxeData: Partial<IBodyTaxUpdate>) => {
    try {
      if (taxe_id.value === null) return;

      const id = toRaw(taxe_id.value);

      const response = await axiosInstance.put<any>(
        `taxes/update/${id}`,
        taxeData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une taxe existante par son identifiant.
   *
   * @async
   * @function deleteTaxById
   * @returns {Promise<any | undefined>}
   *          Réponse de l'API après suppression, ou `undefined` si `taxe_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  deleteTaxById: async () => {
    try {
      if (taxe_id.value === null) return;

      const id = toRaw(taxe_id.value);

      const response = await axiosInstance.delete(`taxes/delete/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
